import { css } from '@emotion/react'
import { i18n } from '@lingui/core'
import { t, Trans } from '@lingui/macro'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import * as React from 'react'

import FilterButton from './FilterButton'
import styles from './FilterModal.module.scss'
import TweakwiseFilterValue from './TweakwiseFilterValue'
import CloseIcon from '../../../../../core/CloseIcon'
import {
    TweakwiseNavigationFacet,
    TweakwiseNavigationPropertiesSortField,
} from '../../../../../graphql/schema.generated'
import Button from '../../../../../input/Button'
import RadioBox from '../../../../../input/RadioBox'
import Container from '../../../../../layout/Container'
import Icon from '../../../../../media/Icon'
import FullscreenNavigationModal from '../../../../../presentation/FullscreenNavigationModal/FullscreenNavigationModal'
import Heading from '../../../../../typography/Heading'
import Text from '../../../../../typography/Text'
import { FilterValues } from '../../ProductFilterPage'
import TweakwiseFilterInput from '../TweakwiseFilterInput'

interface Props {
    sortFields: TweakwiseNavigationPropertiesSortField[]
    isDirty: boolean
    close(): void
    clear(): void
    options: TweakwiseNavigationFacet[]
    values: FilterValues
    setValue(key: string, value: unknown): void
    sortKey: string | undefined
    setSortKey(sortKey: string | undefined): void
    numResults: number
    loading: boolean
}

const FilterModal = ({
    sortFields,
    isDirty,
    close,
    clear,
    options,
    values,
    setValue,
    sortKey,
    setSortKey,
    numResults: _numResults,
    loading,
}: Props) => {
    const scrollContainer = React.useRef<HTMLDivElement>(null)
    const numResults = Intl.NumberFormat(i18n.locale).format(_numResults)

    React.useEffect(() => {
        if (!scrollContainer.current) {
            return
        }

        disableBodyScroll(scrollContainer.current, {
            // allow touch move on any element within an element with the `body-scroll-lock-ignore` attribute
            allowTouchMove: (el) => {
                while (
                    el &&
                    el !== document.body &&
                    typeof el.getAttribute === 'function'
                ) {
                    if (el.getAttribute('body-scroll-lock-ignore') !== null) {
                        return true
                    }
                    el = el.parentNode as HTMLElement
                }
                return
            },
        })

        const container = scrollContainer.current

        return () => {
            if (!container) {
                return
            }

            enableBodyScroll(container)
        }
    }, [scrollContainer])

    const handleSortKeyChange = (
        e: React.ChangeEvent<React.InputHTMLAttributes<HTMLInputElement>>,
    ) => {
        if (!e.target.checked) {
            return
        }
        setSortKey(e.target.value as string)
    }

    const activeSortField = sortFields.find((field) => field.title === sortKey)

    return (
        <FullscreenNavigationModal
            back={
                <Icon
                    component={CloseIcon}
                    wrapperClassName={styles.closeIconWrapper}
                    title={t({
                        id: 'catalog.mobileFilter.modalClose',
                        message: `Close`,
                    })}
                />
            }
            onBack={close}
            title={
                <Heading element="span" variant="h3">
                    <Trans id="catalog.mobileFilter.modalTitle">Filters</Trans>
                </Heading>
            }
            contentClassName={styles.contentContainer}
        >
            <div className={styles.options} ref={scrollContainer}>
                {sortFields.length > 0 && (
                    <FilterButton
                        label={
                            <>
                                <Text
                                    as="span"
                                    css={css`
                                        margin: 0;
                                        font-weight: ${sortKey !== undefined
                                            ? 'bold'
                                            : 'normal'};
                                    `}
                                >
                                    <Trans id="catalog.mobileFilter.sortLabel">
                                        Sort
                                    </Trans>
                                </Text>
                                {activeSortField && (
                                    <Text
                                        as="div"
                                        color="grey"
                                        css={css`
                                            margin: 0;
                                        `}
                                    >
                                        {activeSortField.displayTitle}
                                    </Text>
                                )}
                            </>
                        }
                    >
                        {sortFields.map((sortfield) => (
                            <RadioBox
                                key={sortfield.title}
                                name="sortKey"
                                value={sortfield.title}
                                onChange={handleSortKeyChange}
                                checked={sortfield.isSelected}
                            >
                                {sortfield.displayTitle}
                            </RadioBox>
                        ))}
                    </FilterButton>
                )}

                {options.map((filter) => (
                    <FilterButton
                        key={filter.facetSettings.urlKey}
                        label={
                            <>
                                <Text
                                    as="span"
                                    css={css`
                                        margin: 0;
                                        font-weight: ${values[
                                            filter.facetSettings.urlKey
                                        ] !== undefined
                                            ? 'bold'
                                            : 'normal'};
                                    `}
                                >
                                    {filter.facetSettings.title ||
                                        filter.facetSettings.urlKey}
                                </Text>

                                {values[filter.facetSettings.urlKey] !==
                                    undefined && (
                                    <Text
                                        as="div"
                                        color="grey"
                                        css={css`
                                            margin: 0;
                                        `}
                                    >
                                        <TweakwiseFilterValue
                                            filter={filter}
                                            value={
                                                values[
                                                    filter.facetSettings.urlKey
                                                ]
                                            }
                                        />
                                    </Text>
                                )}
                            </>
                        }
                    >
                        <TweakwiseFilterInput
                            filter={filter}
                            value={values[filter.facetSettings.urlKey]}
                            onChange={(value) =>
                                setValue(filter.facetSettings.urlKey, value)
                            }
                        />
                    </FilterButton>
                ))}
            </div>

            <Container className={styles.results}>
                {isDirty && (
                    <Button
                        variant="secondary"
                        wide
                        onClick={clear}
                        name="Clear filters"
                        category="catalog.productFilterPage.filterBar.clearFilters"
                        css={css`
                            margin-bottom: 10px;
                        `}
                    >
                        <Trans id="catalog.filterListModal.clearFilters">
                            Clear All Filters
                        </Trans>
                    </Button>
                )}

                <Button
                    variant="primary"
                    wide
                    onClick={close}
                    name="See results"
                    category="catalog.productFilterPage.filterBar.seeResults"
                    loading={loading}
                >
                    <Trans id="catalog.filterListModal.seeResults">
                        See {numResults} results
                    </Trans>
                </Button>
            </Container>
        </FullscreenNavigationModal>
    )
}

export default FilterModal
